/**
 * Button — the one CTA component. `bg-brand text-on-solid rounded-xl` was
 * copy-pasted across a dozen files before this existed; new call sites use
 * this instead.
 *
 * Voice rule (DESIGN_SYSTEM.md §6): a control states what it will do, or why
 * it cannot. A disabled Button with a `disabledReason` renders the reason
 * inside the control — "Generate final — 3 fields still needed" — and stays
 * focusable (aria-disabled, click guarded) so keyboard and screen-reader
 * users can reach the explanation. The reasoned-disabled state uses a solid
 * muted treatment, never whole-button opacity: a reason at 1.5:1 defeats the
 * pattern (the tokens.css floor is 4.5:1 for text that must be read).
 */
import type { ButtonHTMLAttributes, ComponentType, MouseEvent, ReactNode } from "react";
import { Loader2 } from "lucide-react";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg" | "xl";

const VARIANT: Record<ButtonVariant, string> = {
  // Solid hover role tokens (never alpha): see --color-brand-hover in
  // tokens.css — hover always moves away from the label's colour.
  primary: "bg-brand text-on-solid hover:bg-brand-hover",
  secondary: "bg-surface border border-border text-text-primary hover:bg-surface-secondary",
  ghost: "text-text-secondary hover:text-text-primary hover:bg-surface-secondary",
  danger: "bg-danger text-on-solid hover:bg-danger-hover",
};

// sm/md/lg mirror the three shipped CTA scales; xl is the standalone-page
// scale (public forms), sized to keep a ≥44px touch target.
const SIZE: Record<ButtonSize, string> = {
  sm: "px-2.5 py-1 rounded-lg text-[13px] gap-1",
  md: "px-3 py-1.5 rounded-lg text-[14px] gap-1.5",
  lg: "px-4 py-1.5 rounded-xl text-[16px] gap-1.5",
  xl: "px-6 py-2.5 rounded-xl text-[16px] gap-2",
};

/** Pure so bun:test can cover every variant/size/state combination. */
export function buttonClasses(
  variant: ButtonVariant,
  size: ButtonSize,
  disabled: boolean,
  extra?: string,
  hasReason = false,
): string {
  const state = disabled
    ? hasReason
      ? // The reason must be readable: solid muted fill (6.08:1 text), no opacity.
        "bg-surface-tertiary text-text-secondary cursor-not-allowed"
      : // No reason to read — conventional faded treatment, identity kept.
        `opacity-50 cursor-not-allowed ${VARIANT[variant].split(" ").filter((c) => !c.startsWith("hover:")).join(" ")}`
    : VARIANT[variant];
  return [
    "inline-flex items-center justify-center font-medium transition-colors",
    // A long reason must wrap, not overflow the layout it is explaining.
    hasReason ? "text-left" : "whitespace-nowrap",
    SIZE[size],
    state,
    extra ?? "",
  ]
    .filter(Boolean)
    .join(" ");
}

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "disabled"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Lucide icon component, rendered before the label at a size-matched scale. */
  icon?: ComponentType<{ size?: number | string }>;
  disabled?: boolean;
  /**
   * Why the control cannot act, shown inside the button when disabled.
   * Required whenever the disabling condition isn't visible right next to
   * the control — the user must never hunt for the blocker.
   */
  disabledReason?: string;
  /**
   * In-flight state: spinner replaces the icon, clicks are guarded, and the
   * control stays focusable (aria-disabled) so focus is not dropped on
   * <body> mid-action. The caller keeps owning the label ("Saving…").
   */
  busy?: boolean;
  children: ReactNode;
}

export function Button({
  variant = "primary",
  size = "md",
  icon: Icon,
  disabled = false,
  disabledReason,
  busy = false,
  children,
  className,
  onClick,
  type = "button",
  ...rest
}: ButtonProps) {
  const blocked = disabled || busy;
  const explains = disabled && !!disabledReason;
  // Focusable-blocked states (reason, busy) guard the click themselves;
  // preventDefault also stops a type="submit" from submitting its form.
  function handleClick(e: MouseEvent<HTMLButtonElement>) {
    if (blocked) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  }
  return (
    <button
      type={type}
      // With a reason (or busy) the button stays in the tab order so the
      // state is perceivable; otherwise native disabled semantics.
      disabled={blocked && !explains && !busy}
      aria-disabled={blocked || undefined}
      aria-busy={busy || undefined}
      onClick={handleClick}
      className={buttonClasses(variant, size, disabled, className, explains)}
      {...rest}
    >
      {busy ? <Loader2 size={size === "sm" ? 13 : 14} className="animate-spin" /> : Icon && <Icon size={size === "sm" ? 13 : 14} />}
      {children}
      {explains && (
        <span className="font-normal"> — {disabledReason}</span>
      )}
    </button>
  );
}
